"use client";

import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { CheckIcon, LoaderIcon, XIcon } from "lucide-react";
import { useForm } from "react-hook-form";

import type { PackageMetadataType } from "@acme/validators";
import { Button } from "@acme/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@acme/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@acme/ui/form";
import { Input } from "@acme/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@acme/ui/select";
import { Textarea } from "@acme/ui/textarea";
import { PackageMetadataSchema } from "@acme/validators";

import { useTRPC } from "~/trpc/react";
import { useSession } from "~/utils/auth";

interface CreatePackageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: PackageMetadataType) => Promise<void>;
  isLoading?: boolean;
}

export function CreatePackageDialog({
  open,
  onOpenChange,
  onSubmit,
  isLoading = false,
}: CreatePackageDialogProps) {
  const { data: session } = useSession();
  const trpc = useTRPC();

  const form = useForm<PackageMetadataType>({
    resolver: zodResolver(PackageMetadataSchema),
    defaultValues: {
      name: "",
      displayName: "",
      description: "",
      author: session?.user.name ?? "",
      license: "MIT",
      repository: undefined,
      homepage: undefined,
      keywords: [],
      category: "codemod",
      visibility: "public",
      access: "public",
    },
  });

  const watchedName = form.watch("name");

  // Validate package name availability
  const nameValidationQuery = useQuery(
    trpc.studio.validatePackageName.queryOptions(
      { name: watchedName, scope: null },
      {
        enabled: watchedName.length > 0 && watchedName.length <= 255,
        retry: false,
      },
    ),
  );

  // Update form default author when session loads
  useEffect(() => {
    if (session?.user.name && !form.getValues("author")) {
      form.setValue("author", session.user.name);
    }
  }, [session?.user.name, form]);

  const handleSubmit = async (data: PackageMetadataType) => {
    try {
      await onSubmit(data);
    } catch {
      // Error handling is done in parent component
    }
  };

  const isNameAvailable = nameValidationQuery.data?.available;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Package from Studio</DialogTitle>
          <DialogDescription>
            Package your codemod and create a pull request project. Fill out the
            package details below.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-6"
          >
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Package Name *</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          placeholder="my-awesome-codemod"
                          {...field}
                          className={
                            watchedName &&
                            (isNameAvailable === false
                              ? "border-red-500"
                              : isNameAvailable === true
                                ? "border-green-500"
                                : "")
                          }
                        />
                        {nameValidationQuery.isLoading && (
                          <LoaderIcon className="text-muted-foreground absolute top-3 right-3 size-4 animate-spin" />
                        )}
                        {!nameValidationQuery.isLoading &&
                          isNameAvailable === true && (
                            <CheckIcon className="absolute top-3 right-3 size-4 text-green-500" />
                          )}
                        {!nameValidationQuery.isLoading &&
                          isNameAvailable === false && (
                            <XIcon className="absolute top-3 right-3 size-4 text-red-500" />
                          )}
                      </div>
                    </FormControl>
                    <FormDescription>
                      Lowercase letters, numbers, and hyphens only
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="displayName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Display Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="My Awesome Codemod" {...field} />
                    </FormControl>
                    <FormDescription>
                      Human-readable name for display
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe what your codemod does..."
                      className="min-h-[80px]"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Brief description of your codemod's purpose
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="author"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Author *</FormLabel>
                    <FormControl>
                      <Input placeholder="Your Name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="license"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>License</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select license" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Private">Private</SelectItem>
                        <SelectItem value="MIT">MIT</SelectItem>
                        <SelectItem value="Apache-2.0">Apache 2.0</SelectItem>
                        <SelectItem value="GPL-3.0">GPL 3.0</SelectItem>
                        <SelectItem value="BSD-3-Clause">
                          BSD 3-Clause
                        </SelectItem>
                        <SelectItem value="ISC">ISC</SelectItem>
                        <SelectItem value="NONE">No License</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="visibility"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Visibility</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="public">Public</SelectItem>
                        <SelectItem value="private">Private</SelectItem>
                        <SelectItem value="org">Organization</SelectItem>
                        <SelectItem value="user">User Only</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="access"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Access</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="public">Public</SelectItem>
                        <SelectItem value="private">Private</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading || isNameAvailable === false}
              >
                {isLoading ? (
                  <>
                    <LoaderIcon className="mr-2 size-4 animate-spin" />
                    Creating Package...
                  </>
                ) : (
                  "Create Package & PR"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
